import qrcode from 'qrcode-generator';
import JsBarcode from 'jsbarcode';
import { t } from './i18n';

export type CodeItemType = 'qrcode' | 'barcode';
export type QrEcLevel = 'L' | 'M' | 'Q' | 'H';
export type BarcodeFormat = 'CODE128' | 'EAN13' | 'EAN8';

export interface CodeDesignItem {
  id?: string;
  type?: string;
  width?: unknown;
  height?: unknown;
  codeData?: unknown;
  fgColor?: unknown;
  bgColor?: unknown;
  ecLevel?: unknown;
  barcodeFormat?: unknown;
  showText?: unknown;
  quietZone?: unknown;
  svg?: unknown;
  [key: string]: unknown;
}

export interface CodeSvgResult {
  svg: string;
  valid: boolean;
  message: string;
}

export interface NormalizedCodeItem {
  type: CodeItemType;
  codeData: string;
  fgColor: string;
  bgColor: string;
  ecLevel: QrEcLevel;
  barcodeFormat: BarcodeFormat;
  showText: boolean;
  quietZone: boolean;
  width: number;
  height: number;
}

const QR_LEVELS = new Set(['L', 'M', 'Q', 'H']);
const BARCODE_FORMATS = new Set(['CODE128', 'EAN13', 'EAN8']);

function text(value: unknown, fallback = ''): string {
  const out = String(value ?? '').trim();
  return out || fallback;
}

function number(value: unknown, fallback: number): number {
  const out = Number(value);
  return Number.isFinite(out) && out > 0 ? out : fallback;
}

export function normalizeColor(value: unknown, fallback: string, allowEmpty = false): string {
  const raw = String(value ?? '').trim();
  if (allowEmpty && raw === '') return '';
  if (/^#[0-9a-f]{6}$/i.test(raw)) return raw.toLowerCase();
  if (/^#[0-9a-f]{3}$/i.test(raw)) {
    return `#${raw[1]}${raw[1]}${raw[2]}${raw[2]}${raw[3]}${raw[3]}`.toLowerCase();
  }
  return fallback;
}

export function normalizeCodeItem(item: CodeDesignItem): NormalizedCodeItem {
  const type = item.type === 'barcode' ? 'barcode' : 'qrcode';
  const ec = String(item.ecLevel ?? 'M').toUpperCase();
  const format = String(item.barcodeFormat ?? 'CODE128').toUpperCase();
  return {
    type,
    codeData: text(item.codeData, type === 'barcode' ? '5901234123457' : 'https://example.com'),
    fgColor: normalizeColor(item.fgColor, '#000000'),
    bgColor: normalizeColor(item.bgColor, '', true),
    ecLevel: (QR_LEVELS.has(ec) ? ec : 'M') as QrEcLevel,
    barcodeFormat: (BARCODE_FORMATS.has(format) ? format : 'CODE128') as BarcodeFormat,
    showText: item.showText !== false,
    quietZone: item.quietZone === true,
    width: number(item.width, type === 'barcode' ? 220 : 140),
    height: number(item.height, type === 'barcode' ? 90 : 140),
  };
}

export function eanChecksum(payload: string): number {
  const digits = payload.split('').map((ch) => Number(ch));
  const sum = digits.reduce((total, digit, index) => total + digit * (index % 2 === 0 ? 1 : 3), 0);
  return (10 - (sum % 10)) % 10;
}

export function validateBarcodeData(data: string, format: BarcodeFormat): { valid: boolean; message: string } {
  if (!data) return { valid: false, message: t('Data is required.') };
  if (format === 'CODE128') return { valid: true, message: '' };
  if (format === 'EAN13') {
    if (!/^\d{13}$/.test(data)) return { valid: false, message: t('EAN13 requires 13 digits.') };
    return Number(data[12]) === eanChecksum(data.slice(0, 12))
      ? { valid: true, message: '' }
      : { valid: false, message: t('EAN13 checksum does not match.') };
  }
  if (!/^\d{8}$/.test(data)) return { valid: false, message: t('EAN8 requires 8 digits.') };
  return Number(data[7]) === eanChecksum(data.slice(0, 7))
    ? { valid: true, message: '' }
    : { valid: false, message: t('EAN8 checksum does not match.') };
}

export function validateCodeItem(item: CodeDesignItem): { valid: boolean; message: string; warning?: string } {
  const normalized = normalizeCodeItem(item);
  if (!normalized.codeData) return { valid: false, message: t('Data is required.') };
  const contrast = checkContrast(normalized.fgColor, normalized.bgColor);
  const warning = contrast.ok ? undefined : contrast.message;
  if (normalized.type === 'barcode') {
    return { ...validateBarcodeData(normalized.codeData, normalized.barcodeFormat), warning };
  }
  return { valid: true, message: '', warning };
}

function relativeLuminance(hex: string): number {
  const channel = (value: number) => {
    const c = value / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

export function checkContrast(fgColor: string, bgColor: string): { ok: boolean; message: string } {
  const fg = normalizeColor(fgColor, '#000000');
  const bg = normalizeColor(bgColor || '#ffffff', '#ffffff');
  const l1 = relativeLuminance(fg);
  const l2 = relativeLuminance(bg);
  const ratio = (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);
  if (ratio < 3) return { ok: false, message: t('Low contrast between colors may prevent scanning.') };
  return { ok: true, message: '' };
}

function escapeXml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function fallbackSvg(item: NormalizedCodeItem, message: string): CodeSvgResult {
  const w = Math.max(1, Math.round(item.width));
  const h = Math.max(1, Math.round(item.height));
  const bg = item.bgColor ? `<rect width="${w}" height="${h}" fill="${item.bgColor}"/>` : '';
  const label = escapeXml(message || t('Invalid code'));
  return {
    valid: false,
    message,
    svg: `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}" role="img" aria-label="${label}">${bg}<rect x="1" y="1" width="${w - 2}" height="${h - 2}" fill="none" stroke="#ef4444" stroke-width="2" stroke-dasharray="6 4"/><text x="${w / 2}" y="${h / 2}" fill="#ef4444" font-family="Arial, sans-serif" font-size="12" text-anchor="middle" dominant-baseline="middle">${label}</text></svg>`,
  };
}

/**
 * Off by default so the layer's box hugs the code; shoppers printing on dark or busy fabric
 * can turn the ISO quiet zone back on (2 modules is the practical minimum scanners accept).
 */
const QR_QUIET_ZONE_MODULES = 2;

function generateQrSvg(item: NormalizedCodeItem): CodeSvgResult {
  try {
    const qr = qrcode(0, item.ecLevel);
    qr.addData(item.codeData);
    qr.make();
    const count = qr.getModuleCount();
    const margin = item.quietZone ? QR_QUIET_ZONE_MODULES : 0;
    const boxSize = count + margin * 2;
    const modules: string[] = [];
    for (let row = 0; row < count; row += 1) {
      for (let col = 0; col < count; col += 1) {
        if (qr.isDark(row, col)) modules.push(`M${col + margin} ${row + margin}h1v1h-1z`);
      }
    }
    const bg = item.bgColor ? `<rect width="${boxSize}" height="${boxSize}" fill="${item.bgColor}"/>` : '';
    return {
      valid: true,
      message: '',
      svg: `<svg xmlns="http://www.w3.org/2000/svg" width="${Math.round(item.width)}" height="${Math.round(item.height)}" viewBox="0 0 ${boxSize} ${boxSize}" shape-rendering="crispEdges">${bg}<path fill="${item.fgColor}" d="${modules.join('')}"/></svg>`,
    };
  } catch (error) {
    return fallbackSvg(item, error instanceof Error ? error.message : t('Could not generate QR code.'));
  }
}

function serializeSvg(svg: SVGSVGElement): string {
  const serializer = typeof XMLSerializer !== 'undefined' ? new XMLSerializer() : null;
  if (serializer) return serializer.serializeToString(svg);
  return svg.outerHTML;
}

function cleanBarcodeSvg(raw: string, item: NormalizedCodeItem): string {
  const width = Math.max(1, Math.round(item.width));
  const height = Math.max(1, Math.round(item.height));
  let out = raw
    .replace(/\s?jsbarcode-[a-z-]+="[^"]*"/g, '')
    .replace(/\s?style="[^"]*"/g, '')
    .replace(/\s?version="[^"]*"/g, '')
    .replace(/\sxmlns="[^"]*"/g, '')
    .replace(/<svg\b([^>]*)>/, `<svg xmlns="http://www.w3.org/2000/svg"$1>`)
    .replace(/\swidth="[^"]*"/, ` width="${width}"`)
    .replace(/\sheight="[^"]*"/, ` height="${height}"`);
  if (!item.bgColor) {
    out = out.replace(/<rect\b[^>]*fill="transparent"\/>/g, '');
  }
  return out;
}

const BARCODE_FONT_SIZE = 16;
/** JsBarcode's default gap between bars and the human-readable text. */
const BARCODE_TEXT_MARGIN = 2;

function generateBarcodeSvg(item: NormalizedCodeItem): CodeSvgResult {
  const validation = validateBarcodeData(item.codeData, item.barcodeFormat);
  if (!validation.valid) return fallbackSvg(item, validation.message);
  if (typeof document === 'undefined' || typeof document.createElementNS !== 'function') {
    return fallbackSvg(item, t('SVG document is not available.'));
  }

  try {
    // No margin, and bars sized to the item: the code fills its box instead of being
    // letterboxed inside JsBarcode's natural width.
    const textHeight = item.showText ? BARCODE_FONT_SIZE + BARCODE_TEXT_MARGIN : 0;
    const render = (barWidth: number) => {
      const node = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      JsBarcode(node, item.codeData, {
        format: item.barcodeFormat,
        lineColor: item.fgColor,
        background: item.bgColor || 'transparent',
        displayValue: item.showText,
        margin: 0,
        width: barWidth,
        height: Math.max(12, Math.round(item.height) - textHeight),
        fontSize: BARCODE_FONT_SIZE,
      });
      return node;
    };
    // Rendered width is linear in bar width (EAN guard digits add a fixed offset), so two
    // probes give the bar width that lands exactly on the item width.
    const widthAt = (barWidth: number) => parseFloat(render(barWidth).getAttribute('width') || '') || 0;
    const w1 = widthAt(1);
    const perBar = widthAt(2) - w1 || w1 || 1;
    const fixed = w1 - perBar;
    const svg = render(Math.max(0.1, (item.width - fixed) / perBar));
    const drawnWidth = parseFloat(svg.getAttribute('width') || '') || item.width;
    svg.setAttribute('width', String(Math.round(item.width)));
    svg.setAttribute('height', String(Math.round(item.height)));
    svg.setAttribute('role', 'img');
    svg.setAttribute('aria-label', `${item.barcodeFormat} barcode ${item.codeData}`);
    // JsBarcode rounds each segment up to whole pixels, so the fit can be a few px off; absorb
    // that sub-percent difference by stretching instead of letterboxing. Bigger gaps (text wider
    // than the bars on a very narrow code) keep proportions.
    const nearFit = Math.abs(drawnWidth - item.width) / item.width < 0.04;
    svg.setAttribute('preserveAspectRatio', nearFit ? 'none' : 'xMidYMid meet');
    return {
      valid: true,
      message: '',
      svg: cleanBarcodeSvg(serializeSvg(svg), item),
    };
  } catch (error) {
    return fallbackSvg(item, error instanceof Error ? error.message : t('Could not generate barcode.'));
  }
}

export function generateCodeSvg(item: CodeDesignItem): CodeSvgResult {
  const normalized = normalizeCodeItem(item);
  if (!normalized.codeData) return fallbackSvg(normalized, t('Data is required.'));
  return normalized.type === 'barcode' ? generateBarcodeSvg(normalized) : generateQrSvg(normalized);
}

/**
 * Thumbnail-only variant: the generated markup carries the layer's pixel size (e.g. 220×90)
 * and, for barcodes, `preserveAspectRatio="none"` so it fills the canvas box exactly. In a small
 * preview tile that overflows or stretches, so scale it to the tile and keep proportions.
 */
export function previewSvg(svg: string): string {
  return svg.replace(/<svg\b[^>]*>/, (tag) => tag
    .replace(/\swidth="[^"]*"/, ' width="100%"')
    .replace(/\sheight="[^"]*"/, ' height="100%"')
    .replace(/\spreserveAspectRatio="[^"]*"/, '')
    .replace(/^<svg/, '<svg preserveAspectRatio="xMidYMid meet"'));
}
